/**
 * DealSidebar — 220px left sidebar shown on deal-context pages.
 * Brand Book §3.1 — bg-surface-subtle, border-right, no rounded corners.
 *
 * Nav sections:
 *   Deals  → All deals · Analysis · Documents · Compare banks
 *   Account → Settings
 *
 * Rendered by MainLayout when dealSidebarCtx is set via useDealSidebar().
 * Hidden on mobile (md: breakpoint).
 */

import { Link } from 'react-router-dom';
import { List, BarChart2, FileText, Shuffle, Settings } from 'react-feather';
import { clsx } from 'clsx';
import { useUIStore } from '../../stores/uiStore';

// ─── Types ───────────────────────────────────────────────────────────────────

type NavItemProps = {
  href: string;
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
};

// ─── Nav item ────────────────────────────────────────────────────────────────

function NavItem({ href, icon, label, active, disabled }: NavItemProps) {
  if (disabled) {
    return (
      <span
        className={clsx(
          'flex items-center gap-2.5 px-4 py-2.5 text-[13px] cursor-not-allowed',
          'text-txt-disabled',
        )}
      >
        <span className="opacity-50">{icon}</span>
        {label}
      </span>
    );
  }

  return (
    <Link
      to={href}
      className={clsx(
        'flex items-center gap-2.5 px-4 py-2.5 text-[13px] transition-colors duration-100',
        active
          ? 'bg-border text-txt-primary'
          : 'text-txt-secondary hover:bg-border hover:text-txt-primary',
      )}
    >
      <span className={clsx('flex-shrink-0', active ? 'opacity-100' : 'opacity-70')}>
        {icon}
      </span>
      {label}
    </Link>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────

function NavSection({ label }: { label: string }) {
  return (
    <div className="px-4 pt-4 pb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-txt-tertiary">
      {label}
    </div>
  );
}

// ─── DealSidebar ─────────────────────────────────────────────────────────────

export function DealSidebar() {
  const ctx = useUIStore((s) => s.dealSidebarCtx);

  // Don't render if no deal context is active
  if (!ctx) {return null;}

  const { dealId, termSheetId, active, isDemo } = ctx;

  // Build nav links based on available IDs
  const analysisHref = isDemo
    ? '/analysis/demo'
    : termSheetId
      ? `/term-sheets/${termSheetId}/analysis`
      : '/deals';

  const extractionHref = termSheetId ? `/term-sheets/${termSheetId}/extraction` : null;

  const negotiationsHref = dealId ? `/deals/${dealId}/negotiations` : null;

  const iconSize = 16;
  const iconStroke = 1.75;

  return (
    <aside
      className={clsx(
        // Hidden on mobile — BottomNav handles that
        'hidden md:flex flex-col flex-shrink-0',
        'w-[220px] bg-surface-subtle border-r border-border',
        'sticky top-[56px] h-[calc(100vh-56px)] overflow-y-auto',
      )}
      aria-label="Deal navigation"
    >
      <NavSection label="Deal" />

      <NavItem
        href="/deals"
        icon={<List size={iconSize} strokeWidth={iconStroke} />}
        label="All deals"
        active={false}
      />

      <NavItem
        href={analysisHref}
        icon={<BarChart2 size={iconSize} strokeWidth={iconStroke} />}
        label="Analysis"
        active={active === 'analysis'}
      />

      <NavItem
        href={extractionHref ?? '#'}
        icon={<FileText size={iconSize} strokeWidth={iconStroke} />}
        label="Extraction"
        active={active === 'extraction'}
        disabled={!extractionHref}
      />

      <NavItem
        href={negotiationsHref ?? '#'}
        icon={<Shuffle size={iconSize} strokeWidth={iconStroke} />}
        label="Compare banks"
        active={active === 'negotiations'}
        disabled={!negotiationsHref}
      />

      <NavSection label="Account" />

      <NavItem
        href="/settings"
        icon={<Settings size={iconSize} strokeWidth={iconStroke} />}
        label="Settings"
        active={false}
      />
    </aside>
  );
}
